import React, { useMemo, useState } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const CHIPS = {
  not_started:        { label: "Not Started", cls: "border-white/15 text-nex-muted" },
  pending:            { label: "Pending",     cls: "border-yellow-400/40 text-yellow-300" },
  resident_submitted: { label: "Submitted",   cls: "border-sky-400/40 text-sky-300" },
  staff_assessed:     { label: "Assessed",    cls: "border-emerald-400/40 text-emerald-300" },
};
const FILTERS = [["all", "All"], ...Object.entries(CHIPS).map(([k, v]) => [k, v.label])];

export default function PillarResidentDropdown({ pillarId, residents, assignments, onOpen }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    const latest = {};
    assignments.forEach((a) => {
      const cur = latest[a.resident_id];
      if (!cur || (a.assigned_at || "") > (cur.assigned_at || "")) latest[a.resident_id] = a;
    });
    return residents.map((r) => ({ r, name: `${r.first_name} ${r.last_name}`, status: CHIPS[latest[r.id]?.status] ? latest[r.id].status : "not_started" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [residents, assignments]);

  const counts = Object.fromEntries(FILTERS.map(([k]) => [k, k === "all" ? rows.length : rows.filter((x) => x.status === k).length]));
  const list = rows.filter((x) => (filter === "all" || x.status === filter) && x.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <section data-testid={`pillar-residents-${pillarId}`} className="border border-white/10 rounded-sm bg-white/[0.02]">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`pillar-residents-toggle-${pillarId}`} aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/5 transition-colors">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-silver">Residents · {rows.length}</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-nex-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-white/5">
          <div className="p-3 space-y-2">
            <input data-testid={`pillar-residents-search-${pillarId}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents…"
              className="w-full px-3 py-2 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm rounded-sm" />
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`pillar-residents-filter-${pillarId}-${k}`} aria-pressed={filter === k}
                  className={`px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors ${
                    filter === k ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver hover:border-white/30"}`}>
                  {l} · {counts[k]}
                </button>
              ))}
            </div>
          </div>
          <ul data-testid={`pillar-residents-list-${pillarId}`} className="max-h-72 overflow-y-auto divide-y divide-white/5 border-t border-white/5">
            {list.map(({ r, name, status }) => (
              <li key={r.id}>
                <button type="button" onClick={() => onOpen(r, name)} data-testid={`pillar-residents-row-${pillarId}-${r.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-nex-gold/5 transition-colors">
                  <span className="flex-1 min-w-0 truncate text-sm text-nex-silver">{name}</span>
                  <OpenResidentButton residentId={r.id} name={name} testid={`open-resident-pdd-${pillarId}-${r.id}`} />
                  <span data-testid={`pillar-residents-chip-${pillarId}-${r.id}`}
                    className={`inline-flex px-2 py-0.5 rounded-full border font-mono text-[9px] tracking-[0.2em] uppercase ${CHIPS[status].cls}`}>
                    {CHIPS[status].label}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-nex-muted" />
                </button>
              </li>
            ))}
            {!list.length && <li data-testid={`pillar-residents-empty-${pillarId}`} className="px-4 py-6 text-center text-xs text-nex-muted">No residents match.</li>}
          </ul>
        </div>
      )}
    </section>
  );
}
